// src/pages/auth/ForgotPasswordPage.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Mail, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import authApi from 'api/authApi';   // adjust path if needed
// import Logo from 'assets/images/logo.png';
import Logo from 'assets/logo.png';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailNotFound, setEmailNotFound] = useState(false);   // <-- new state

  const validateIdentifier = (value) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const codeRegex = /^[A-Z0-9]+$/;
    return emailRegex.test(value) || codeRegex.test(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!identifier.trim()) {
      setError('Email is required');
      return;
    }

    if (!validateIdentifier(identifier)) {
      setError('Please enter a valid email');
      return;
    }

    setIsLoading(true);
    setError('');
    setEmailNotFound(false);

    try {
      await authApi.requestPasswordReset(identifier);

      setIsLoading(false);
      setEmailSent(true);
      toast.success('Password reset link sent! Check your email.');
    } catch (err) {
      setIsLoading(false);

      const isNotFound = err.isNotFound === true;
      const msg = err.message || 'Failed to send reset link.';

      setError(msg);
      toast.error(msg);

      if (isNotFound) {
        setEmailNotFound(true);   // <-- show "Try Another Way"
      }
    }
  };

  const handleLoginAnotherWay = () => {
    navigate('/security-question-login');
  };

  /* -------------------------- EMAIL SENT (FOUND) -------------------------- */
  if (emailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/30 p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8 animate-fade-in">
            <div className="inline-flex items-center justify-center w-full mx-auto rounded-2xl bg-transparent mb-4 p-2">
              <img src={Logo} alt="Shree Sati 360 Logo" className="w-full h-full object-contain" />
            </div>
            {/* <h1 className="text-3xl font-bold font-['Space_Grotesk'] mb-2">Check Your Email</h1> */}
          </div>

          <Card className="shadow-xl animate-slide-in">
            <CardHeader>
              <CardTitle className="text-2xl text-center">Email Sent!</CardTitle>
              <CardDescription className="text-center">
                We've sent a reset link to <span className="font-semibold">{identifier}</span>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground text-center">
                Click the link in your email to create a new password.
              </p>
              <Button onClick={() => navigate('/login')} className="w-full">
                Back to Login
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  /* -------------------------- MAIN FORM UI -------------------------- */
  return (
    <div className="min-h-screen flex items-center justify-center  p-4 bg-[#2a3646]">
      <div className="w-full max-w-md">
        <div className="text-center mb-8 animate-fade-in">
          {/* <div className="inline-flex items-center justify-center w-full mx-auto rounded-2xl bg-white mb-4 shadow-lg p-2">
            <img src={Logo} alt="MVT CRM Logo" className="w-full h-full object-contain" />
          </div> */}
          <h1 className="text-3xl font-bold font-['Space_Grotesk'] mb-2 text-white">Forgot Password?</h1>
          <p className="text-muted-foreground text-white">No worries, we'll send you reset instructions.</p>
        </div>

        <Card className="shadow-xl animate-slide-in">
          <CardHeader>
            <CardTitle className="text-2xl">Reset Password</CardTitle>
            <CardDescription>
              Enter your email or employee code and we'll send you a reset link
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="identifier">Email Code</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="identifier"
                    type="email"
                    placeholder="company@gmail.com"
                    value={identifier}
                    onChange={(e) => {
                      setIdentifier(e.target.value);
                      setError('');
                      setEmailNotFound(false);
                    }}
                    className={cn('pl-10', error && 'form-error')}
                  />
                </div>
                {error && <p className="text-sm text-destructive">{error}</p>}
              </div>

              <Button type="submit" className="w-full h-12 bg-[#2a3646]" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send Reset Link'
                )}
              </Button>

              {/* Show "Try Another Way" only if email not found */}
              {/* {emailNotFound && (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-12"
                  onClick={handleLoginAnotherWay}
                >
                  Try Another Way
                </Button>
              )} */}

              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => navigate('/login')}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Login
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;